import * as React from 'react';

import Media from '@uizard-io/styled-media-query';
import Image, { GatsbyImageProps } from 'gatsby-image';
import Styled from 'styled-components/macro';

import Button from 'components/buttons/Button';
import Content from 'components/layout/Content/Content';
import MediaQuery from 'components/layout/MediaQuery';
import Headline from 'components/texts/Headline';
import RichDescription from 'components/texts/RichDescription';
import Tagline from 'components/texts/Tagline';

const Container = Styled.div`
  margin: 6rem 0 6rem 0;
  overflow: hidden; // fix for columns overflow

   ${Media.greaterThan('small')`
     margin: 10rem 0 10rem 0;
  `}
  
  ${Media.greaterThan('medium')`
     margin: 15rem 0 15rem 0;
  `}
`;

const Columns = Styled.div`
  ${Media.greaterThan('700px')`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 42rem;
  `}
  
  ${Media.greaterThan('large')`
    height: 51.3rem;
  `}
`;

const Column = Styled.div`
  position: relative;
  
  ${Media.greaterThan('700px')`
    width: 45%;
    min-width: 0;
  `}
`;

const ColumnIllustration = Styled(Column)`
  height: 20rem;
  margin-top: 10rem;
  width: 100%;
  display: flex;
  align-items: center;

  ${Media.greaterThan('medium')`
    height: 42rem;
    margin-top: 15rem;
  `}
      
  ${Media.greaterThan('700px')`
      height: auto;
      width: 50%;
      margin-top: 0;
  `}
  
  position: relative;
`;

const Text = Styled.div`
  width: 43rem;
  max-width: 100%;

  ${Media.greaterThan('large')`
    width: 100%;
    padding-left: 9.5rem;
  `}
`;

const RichDescriptionExtended = Styled(RichDescription)`
  margin-bottom: 5rem;

  ${Media.greaterThan('700px')`
    margin-bottom: 0;
  `}
`;

const TaglineExtended = Styled(Tagline)`
  color: #7f7f7f;
`;

const w = 488;
const h = 310;

const VideoContainer = Styled.div`
  position: relative;
  height: 0;
  width: ${w}px;
  max-width: 100%;
  padding-bottom: ${(props) => (props.videoH ? Math.round((props.videoH / w) * 100) : Math.round((h / w) * 100))}%;
  margin: 0 auto;
  overflow: hidden;
  
  ${Media.greaterThan('medium')`
    margin: 0;
  `}
`;

const ImgContainer = Styled.div`

`;

const ImageMobile = Styled(Image)`
`;

const ImageDesktop = Styled(Image)`
  width: 100%;
`;

const VideoContent = Styled.div`
  position: absolute;
  top: -20px;
  left: 0;
  right: 0;
  bottom: 0;
`;

const Video = Styled.video`
  position: absolute;
  z-index: 1;
  left: 0;
  bottom: 0;
  width: 100%;
`;

const ExtendedButton = Styled(Button)`
  margin: 0 0 3rem 0;

  ${Media.greaterThan('medium')`
    margin: 5rem 0 5rem 0;
  `}
`;

const NavButtonLink = Styled.a`
  display: block;
`;

type HeroBigTextBannerWithVideoProps = {
  tagline?: string;
  headline: string;
  description: string;
  imageA: GatsbyImageProps;
  imageB: GatsbyImageProps;
  imageAlt: string;
  videoName: string;
  link?: string;
  cta?: string;
  tagAsTitle?: boolean;
  videoH?: number;
};

export default ({
  tagline,
  headline,
  description,
  imageA,
  imageB,
  imageAlt,
  videoName,
  link,
  cta,
  tagAsTitle,
  videoH,
}: HeroBigTextBannerWithVideoProps) => (
  <Container>
    <Content>
      <Columns>
        <Column>
          <Text>
            {tagline && tagAsTitle && (
              <TaglineExtended as="h1">{tagline}</TaglineExtended>
            )}
            {tagAsTitle && <Headline as="h2">{headline}</Headline>}

            {tagline && !tagAsTitle && (
              <TaglineExtended>{tagline}</TaglineExtended>
            )}
            {!tagAsTitle && <Headline>{headline}</Headline>}

            <RichDescriptionExtended content={description} />

            {link && cta && cta.indexOf('Sign up') !== -1 && (
              <NavButtonLink href={link}>
                <ExtendedButton solid>{cta || 'Read more'}</ExtendedButton>
              </NavButtonLink>
            )}

            {(link && cta === undefined)
              || (cta && cta.indexOf('Sign up') === -1 && (
                <NavButtonLink href={link}>
                  <ExtendedButton variant="primarylight" solid>
                    {cta || 'Read more'}
                  </ExtendedButton>
                </NavButtonLink>
              ))}
          </Text>
        </Column>

        <ColumnIllustration>
          <VideoContainer videoH={videoH}>
            <VideoContent>
              <Video autoPlay muted loop playsInline>
                <source
                  src={`/videos/${videoName}.mp4`}
                  type="video/mp4"
                />
                <source
                  src={`/videos/${videoName}.ogv`}
                  type="video/ogv"
                />
                <source
                  src={`/videos/${videoName}.webm`}
                  type="video/webm"
                />
                <img
                  src={`/videos/${videoName}.png`}
                  alt={`Video of ${imageAlt}`}
                />
              </Video>
            </VideoContent>

            <ImgContainer>
              <MediaQuery lessThan="medium">
                <ImageMobile
                  fluid={'fluid' in imageB ? imageB.fluid : null}
                  alt={imageAlt}
                />
              </MediaQuery>

              <MediaQuery greaterThan="medium">
                <ImageDesktop
                  fluid={'fluid' in imageA ? imageA.fluid : ''}
                  alt={imageAlt}
                />
              </MediaQuery>
            </ImgContainer>
          </VideoContainer>
        </ColumnIllustration>
      </Columns>
    </Content>
  </Container>
);
